import { hasRecoverableMsalLocalState } from "@/services/msalSessionState";

export const AUTH_RECOVERY_MIN_WAIT_MS = 12000;

export interface AuthHintSnapshot {
    hasExternalUserData: boolean;
    hasMsalData: boolean;
    hasAuthStateMarker: boolean;
    hasLogoutEvent: boolean;
    wasRecentlyAuthenticated: boolean;
}

export function normalizeAuthPath(pathname: string | null | undefined): string {
    if (!pathname) {
        return "/";
    }

    if (pathname.endsWith("/") && pathname !== "/") {
        return pathname.slice(0, -1);
    }

    return pathname;
}

export function isStandaloneDisplayRoute(
    pathname: string | null | undefined
): boolean {
    return normalizeAuthPath(pathname).startsWith("/display/");
}

export function isPublicEntryRoute(
    pathname: string | null | undefined
): boolean {
    const normalizedPath = normalizeAuthPath(pathname);

    return (
        normalizedPath === "/" ||
        normalizedPath === "/externalLogin" ||
        normalizedPath.startsWith("/auth")
    );
}

export function isProtectedAppRoute(
    pathname: string | null | undefined
): boolean {
    return !isStandaloneDisplayRoute(pathname) && !isPublicEntryRoute(pathname);
}

export function hasRecoverableAuthHints(snapshot: AuthHintSnapshot): boolean {
    if (snapshot.hasLogoutEvent) {
        return false;
    }

    return (
        snapshot.hasExternalUserData ||
        snapshot.hasMsalData ||
        snapshot.hasAuthStateMarker ||
        snapshot.wasRecentlyAuthenticated
    );
}

export function computeAuthRequiredDelay(
    waitTime: number,
    snapshot: AuthHintSnapshot,
    minRecoveryWaitMs: number = AUTH_RECOVERY_MIN_WAIT_MS
): number {
    const normalizedWait =
        Number.isFinite(waitTime) && waitTime > 0 ? waitTime : 0;

    if (!hasRecoverableAuthHints(snapshot)) {
        return normalizedWait;
    }

    return Math.max(normalizedWait + 1500, minRecoveryWaitMs);
}

export function readAuthHintSnapshot(
    storage: Storage | null | undefined,
    wasRecentlyAuthenticated: boolean
): AuthHintSnapshot {
    const emptySnapshot: AuthHintSnapshot = {
        hasExternalUserData: false,
        hasMsalData: false,
        hasAuthStateMarker: false,
        hasLogoutEvent: false,
        wasRecentlyAuthenticated,
    };

    if (!storage) {
        return emptySnapshot;
    }

    try {
        const hasExternalUserData = !!storage.getItem("externalUserProfile");
        const hasMsalData = hasRecoverableMsalLocalState(storage);
        const hasLogoutEvent = !!storage.getItem("app_logout_event");
        let hasAuthStateMarker = false;

        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (!key) continue;

            if (key === "app_auth_state") {
                try {
                    const parsed = JSON.parse(storage.getItem(key) || "{}");
                    hasAuthStateMarker = parsed?.authenticated === true;
                } catch {
                    hasAuthStateMarker = true;
                }
            }

            if (hasAuthStateMarker) {
                break;
            }
        }

        return {
            hasExternalUserData,
            hasMsalData,
            hasAuthStateMarker,
            hasLogoutEvent,
            wasRecentlyAuthenticated,
        };
    } catch {
        return emptySnapshot;
    }
}
